import {
  Accordion,
  Square,
  Adapt,
  Popover,
  Sheet,
  SizableText,
  YStack,
  Stack,
  interaction,
  StackProps,
  XStack,
  Circle,
  isWeb,
} from '@mythical/ui'
import {
  IconCheck,
  IconChevronDown,
  IconFaders,
} from 'app/components/primitive/Icon'
import {
  SearchFilters as SearchFiltersType,
  useSearchContext,
} from './SearchContext'
import {
  createSearchCategoriesQuery,
  mapToSearchCategories,
} from 'app/queries/search/search-categories'
import useQuery from 'app/hooks/use-query'
import { useState } from 'react'
import { useSafeArea } from 'app/provider/safe-area/use-safe-area'
import { Button } from 'app/components/primitive/Button'
import { Trash } from '@tamagui/lucide-icons'
import { IconButton } from 'app/components/primitive/IconButton'

interface SearchFiltersProps {}

interface FilterOption {
  label: string
  value: string
}

const categoriesQuery = createSearchCategoriesQuery()
const categoriesParams = {}

const sorts = [
  { label: 'Newest', value: '-publishedAt' },
  { label: 'Oldest', value: 'publishedAt' },
  { label: 'Most Comments', value: '-commentCount' },
  { label: 'Highest Rated', value: '-rating' },
]

const contentTypes = [
  { label: 'Videos', value: 'video' },
  { label: 'Text', value: 'text' },
  { label: 'Image', value: 'image' },
]

const getYearOptions = (
  startYear = 2019,
  endYear = new Date().getFullYear(),
) => {
  const years = Array.from(
    { length: endYear - startYear + 1 },
    (_, index) => startYear + index,
  )

  const yearOptions = years.map((year) => ({
    label: String(year),
    value: String(year),
  }))

  return yearOptions
}

const yearOptions = getYearOptions()

const toFilterOptions = (
  values: { name: string; id: string }[] | undefined,
): FilterOption[] => {
  return (
    values?.map((value) => ({
      label: value.name,
      value: value.id,
    })) || []
  )
}

const SearchFilters = ({}: SearchFiltersProps) => {
  const [openFilter, setOpenFilter] = useState<string>('sortBy')
  const filters = useSearchContext((state) => state.filters)
  const hasActiveFilters = useSearchContext((state) => state.hasActiveFilters)
  const setFilter = useSearchContext((state) => state.setFilter)
  const clearFilters = useSearchContext((state) => state.clearFilters)
  const insets = useSafeArea()

  const searchCategories = useQuery(
    categoriesQuery,
    categoriesParams,
    mapToSearchCategories,
  )

  const categoryOpts = toFilterOptions(searchCategories?.data?.categories)
  const collectionOpts = toFilterOptions(searchCategories?.data?.collections)
  const memberTierOpts = toFilterOptions(searchCategories?.data?.memberTiers)

  const handleChangeFilter = (
    filter: keyof SearchFiltersType,
    value: string,
  ) => {
    if (filters[filter] === value) {
      setFilter(filter, '')
    } else {
      setFilter(filter, value)
    }

    setOpenFilter('')
  }

  return (
    <Popover size="$3.5" stayInFrame resize allowFlip offset={12}>
      <Popover.Trigger asChild>
        <Button
          theme={hasActiveFilters ? 'white' : 'outlineSecondary'}
          circular
          icon={<IconFaders size={24} />}
          size={40}
        />
      </Popover.Trigger>

      <Adapt when="xs">
        <Sheet
          animation="medium"
          modal
          snapPointsMode="percent"
          snapPoints={[70]}
          dismissOnSnapToBottom
        >
          <Sheet.Overlay
            bg="$backdrop"
            animation="quick"
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
          />

          <Sheet.Frame theme="bgSecondary">
            <Sheet.Handle bg="$borderSecondary" top="$1.5" />
            <Sheet.ScrollView p="$3.5">
              <Adapt.Contents />
            </Sheet.ScrollView>
          </Sheet.Frame>
        </Sheet>
      </Adapt>

      <Popover.Content
        theme="bgSecondary"
        width={354}
        enterStyle={{ y: -10, opacity: 0 }}
        exitStyle={{ y: -10, opacity: 0 }}
        elevate
        br="$8"
        p="$0"
        py="$3.5"
        px="$2"
        mx="$2"
        animation={['quick', { opacity: { overshootClamping: true } }]}
        {...!isWeb && { maxHeight: 800 }}
      >
        <Popover.ScrollView w="100%" px="$2">
          <YStack h="100%" w="100%" $xs={{ pb: insets.bottom + 8 }}>
            <XStack jc="center" ai="center" pb="$2" pt="$2" gap="$1" mih={45}>
              <SizableText ta="center" fow="500">
                Sort & Filter
              </SizableText>
              {hasActiveFilters && (
                <IconButton size="$2" onPress={() => clearFilters()}>
                  <Trash size={18} />
                </IconButton>
              )}
            </XStack>
            <Accordion
              overflow="hidden"
              width="100%"
              p="$0"
              type="single"
              collapsible
              value={openFilter}
              onValueChange={setOpenFilter}
            >
              <AccordionItem
                title="Sort By"
                accordionValue="sortBy"
                value={filters.sort}
                options={sorts}
                onChange={(value) => handleChangeFilter('sort', value)}
                defaultValue="-publishedAt"
              />
              <AccordionItem
                title="Categories"
                accordionValue="categories"
                value={filters.category}
                options={categoryOpts}
                onChange={(value) => handleChangeFilter('category', value)}
              />
              <AccordionItem
                title="Collections"
                accordionValue="collections"
                value={filters.collection}
                options={collectionOpts}
                onChange={(value) => handleChangeFilter('collection', value)}
              />
              <AccordionItem
                title="Member Tiers"
                accordionValue="memberTiers"
                value={filters.contentTier}
                options={memberTierOpts}
                onChange={(value) => handleChangeFilter('contentTier', value)}
              />
              <AccordionItem
                title="Content Types"
                accordionValue="contentTypes"
                value={filters.contentType}
                options={contentTypes}
                onChange={(value) => handleChangeFilter('contentType', value)}
              />
              <AccordionItem
                title="Years"
                accordionValue="year"
                value={filters.year}
                options={yearOptions}
                onChange={(value) => handleChangeFilter('year', value)}
                bbw={0}
              />
            </Accordion>
          </YStack>
        </Popover.ScrollView>
      </Popover.Content>
    </Popover>
  )
}

export default SearchFilters

interface AccordionItemProps extends StackProps {
  title: string
  accordionValue: string
  value: string
  onChange: (value: string) => void
  options: FilterOption[]
  defaultValue?: string
}

const AccordionItem = ({
  title,
  accordionValue,
  value,
  options,
  onChange,
  defaultValue,
  ...props
}: AccordionItemProps) => {
  const isActive = defaultValue ? value !== defaultValue : !!value
  return (
    <Accordion.Item
      value={accordionValue}
      bbw={1}
      bbc="$borderSecondary"
      {...props}
    >
      <Accordion.Trigger
        py="$3.5"
        px="$0"
        flexDirection="row"
        jc="space-between"
        {...interaction.opacity}
      >
        {({ open }: { open: boolean }) => (
          <>
            <XStack ai="center" jc="center" gap="$2">
              {isActive && <Circle size={12} bg="$fgPrimary" />}
              <SizableText size="$3" fow="500" color="$fgSecondary">
                {title}
              </SizableText>
            </XStack>
            <Square animation="quick" rotate={open ? '180deg' : '0deg'}>
              <IconChevronDown size={18} color="$fgSecondary" />
            </Square>
          </>
        )}
      </Accordion.Trigger>

      <Accordion.HeightAnimator animation="medium">
        <Accordion.Content p="$0" animation="medium" exitStyle={{ opacity: 0 }}>
          <YStack role="radiogroup" aria-label="Sort options">
            {options.map((option) => (
              <Stack
                key={option.value}
                tag="button"
                role="radio"
                aria-checked={value === option.value}
                flexDirection="row"
                jc="space-between"
                ai="center"
                gap="$2"
                py="$3"
                onPress={() => onChange(option.value)}
                {...interaction.opacity}
              >
                <SizableText size="$3" mih={20} fow="500" color="$fgPrimary">
                  {option.label}
                </SizableText>
                {value === option.value && <IconCheck size={20} aria-hidden />}
              </Stack>
            ))}
          </YStack>
        </Accordion.Content>
      </Accordion.HeightAnimator>
    </Accordion.Item>
  )
}
